'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styles from './design-assets.module.css';
import { getDesignAssetHome, type DesignAssetGroupPreview } from './api';
import { buildDesignAssetPath } from './route-path';
import logoEn from './_images/ssv-logo-en.png';
import {
  DesignEid,
  dtElementParams,
  useDesignHomeDtAttrs,
} from './tracking';

// 标题未配置时的默认展示，与后台配置口径保持一致。
const defaultHomeTitle = 'Tencent SSV\nBrand\nGuidelines';

interface WeixinJSBridgeAPI {
  invoke: (
    method: string,
    params: Record<string, never>,
    callback: () => void
  ) => void;
}

function playMutedVideo(video: HTMLVideoElement | null) {
  if (!video) return;
  video.muted = true;
  video.defaultMuted = true;
  try {
    const result = video.play();
    if (result && typeof result.then === 'function') {
      void result.catch(() => undefined);
    }
  } catch {
    // 由微信桥接、媒体就绪事件或下一次用户手势继续重试。
  }
}

export default function DesignAssetsPage() {
  const [title, setTitle] = useState(defaultHomeTitle);
  const [description, setDescription] = useState('');
  const [groups, setGroups] = useState<DesignAssetGroupPreview[]>([]);
  const [backgroundURL, setBackgroundURL] = useState('');
  const [backgroundType, setBackgroundType] = useState(0);
  const [backgroundDimmed, setBackgroundDimmed] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [videoAttempt, setVideoAttempt] = useState(0);
  const videoFailed = videoAttempt >= 2;
  const videoReadyRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [loading, setLoading] = useState(true);
  const pageDtAttrs = useDesignHomeDtAttrs();

  // 真机(尤其 iOS Safari / 部分 Android Chrome)出现"视频加载成功但画面黑屏"
  // 通常源于编码不支持 / Range 失败 / 静默解码失败,error 事件不会触发。
  // 此处用一个首帧超时做兜底:超过 6s 仍未触发 loadeddata/canplay 即视为渲染失败,
  // 配合现有 videoAttempt 走与图片一致的二次重试逻辑。
  useEffect(() => {
    videoReadyRef.current = false;
    if (!backgroundURL || backgroundType !== 2 || videoFailed) return;
    const timer = window.setTimeout(() => {
      if (!videoReadyRef.current) {
        setVideoAttempt((value) => value + 1);
      }
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [backgroundURL, backgroundType, videoAttempt, videoFailed]);

  // 微信内置浏览器(Android X5 内核 / iOS WKWebView)对 autoplay 限制严格,
  // 即便 muted + playsInline 也可能在挂载时被静默拦截。
  // 这里采用「挂载即播 + 首次手势兜底 + WeixinJSBridgeReady 兜底」:
  // 挂载时立即 play() 让普通浏览器立刻自动播放;
  // 微信 iOS 在 WeixinJSBridge 未就绪前会拒绝 play,需等 bridge ready 再重试;
  // 微信拦截场景下挂载期 play() 被静默拒绝,需要等首次用户手势(touchstart/click)
  // 在手势上下文内再次调用 play() 才能成功。注意:play() 被拒不再触发 videoAttempt
  // 重挂载,避免在微信里把视频彻底弄消失。
  useEffect(() => {
    if (!backgroundURL || backgroundType !== 2 || videoFailed) return;
    const tryPlay = () => {
      playMutedVideo(videoRef.current);
    };
    const tryPlayThroughWeixinBridge = () => {
      const bridge = (
        window as typeof window & { WeixinJSBridge?: WeixinJSBridgeAPI }
      ).WeixinJSBridge;
      if (!bridge) return false;
      try {
        // 背景配置异步返回时 bridge ready 往往已经发生；必须重新进入
        // 微信桥回调上下文，普通的 play() 调用拿不到自动播放权限。
        bridge.invoke('getNetworkType', {}, tryPlay);
        return true;
      } catch {
        return false;
      }
    };
    function removeGestureListeners() {
      window.removeEventListener('touchstart', onFirstGesture);
      window.removeEventListener('click', onFirstGesture);
    }
    function onFirstGesture() {
      tryPlay();
    }
    function onWeixinBridgeReady() {
      if (!tryPlayThroughWeixinBridge()) {
        tryPlay();
      }
      document.removeEventListener(
        'WeixinJSBridgeReady',
        onWeixinBridgeReady
      );
    }
    const video = videoRef.current;
    // play() 的返回值和 paused 状态在微信 WebView 中可能提前变化，
    // 只有 playing 事件能确认媒体已真正开始推进。
    video?.addEventListener('playing', removeGestureListeners);
    tryPlay();
    window.addEventListener('touchstart', onFirstGesture, { passive: true });
    window.addEventListener('click', onFirstGesture);
    // 微信 iOS 内置浏览器:挂载期 WeixinJSBridge 可能尚未就绪,
    // 此时调 play() 会被静默拒绝。监听 WeixinJSBridgeReady 再次重试。
    if (typeof window !== 'undefined') {
      if (!tryPlayThroughWeixinBridge()) {
        document.addEventListener(
          'WeixinJSBridgeReady',
          onWeixinBridgeReady
        );
      }
    }
    return () => {
      video?.removeEventListener('playing', removeGestureListeners);
      removeGestureListeners();
      document.removeEventListener('WeixinJSBridgeReady', onWeixinBridgeReady);
    };
  }, [backgroundURL, backgroundType, videoAttempt, videoFailed]);

  useEffect(() => {
    getDesignAssetHome()
      .then((data) => {
        setTitle(data.title || defaultHomeTitle);
        setDescription(data.description || '');
        setBackgroundURL(data.background_url || '');
        setBackgroundType(Number(data.background_type) || 0);
        setBackgroundDimmed(Boolean(data.background_dimmed));
        if (Array.isArray(data.groups)) {
          setGroups(
            data.groups.map((group) => ({
              ...group,
              assets: group.first_asset_id
                ? [
                    {
                      group_id: group.group_id,
                      asset_id: group.first_asset_id,
                      group_short_code: group.group_short_code,
                      asset_short_code: group.first_asset_short_code,
                      asset_name: group.group_name,
                      description: '',
                    },
                  ]
                : [],
            })),
          );
        }
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className={styles.homeLoading} aria-busy="true">
        <span className={styles.loadingIndicator} aria-label="内容加载中" />
      </main>
    );
  }

  return (
    <main className={styles.homePage} {...pageDtAttrs}>
      <section className={styles.hero}>
        {backgroundURL && backgroundType === 2 && !videoFailed ? (
          <video
            key={videoAttempt}
            ref={videoRef}
            className={styles.heroMedia}
            autoPlay
            loop
            muted
            playsInline
            // 兼容老版 iOS WKWebView
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ 'webkit-playsinline': '' } as any)}
            // 微信 Android X5 内核:不声明 x5-video-player-type 时,
            // X5 会用自己的"腾讯视频播放器"接管,常见表现为无声黑屏。
            x5-video-player-type="h5"
            x5-playsinline=""
            preload="auto"
            // 参考 S 大会 banner:视频未就绪时显示 poster,避免黑屏。
            poster={backgroundURL}
            src={videoAttempt > 0 ? `${backgroundURL}${backgroundURL.includes('?') ? '&' : '?'}_retry=${videoAttempt}` : backgroundURL}
            onCanPlay={() => {
              videoReadyRef.current = true;
            }}
            onLoadedData={(event) => {
              videoReadyRef.current = true;
              // Android 微信 X5 可能丢弃媒体就绪前发出的 play()，
              // 与 S 大会背景视频一致，在首帧数据就绪后再次启动。
              playMutedVideo(event.currentTarget);
            }}
            onEnded={() => {
              // 微信 X5 内核 + HEVC 编码下 loop 属性常被静默忽略,
              // 视频播完停在最后一帧不再循环。补 ended 兜底:currentTime 归零并重播。
              const video = videoRef.current;
              if (!video) return;
              try {
                video.currentTime = 0;
              } catch {
                // 部分微信内核在 seeking 时抛错,忽略。
              }
              const result = video.play();
              if (result && typeof result.then === 'function') {
                result.catch(() => undefined);
              }
            }}
            onError={() => setVideoAttempt((value) => value + 1)}
          />
        ) : null}
        {backgroundURL && backgroundType === 1 ? (
          <div className={styles.heroMedia} style={{ backgroundImage: `url(${backgroundURL})` }} />
        ) : null}
        {backgroundDimmed ? <div className={styles.heroMask} /> : null}
        <div className={styles.heroInner}>
          <div className={styles.heroMain}>
            {title.trim() ? (
              <h1>
                {title.split(/\r?\n/).map((line, index) => (
                  <span className={styles.heroTitleLine} key={`${line}-${index}`}>
                    {line || '\u00a0'}
                  </span>
                ))}
              </h1>
            ) : null}
            {description ? <p>{description}</p> : null}

            <div className={`${styles.cards}${groups.length === 1 ? ` ${styles.cardsOneCol}` : groups.length === 2 || groups.length === 4 ? ` ${styles.cardsTwoCol}` : ''}`}>
              {groups.map((group) => {
                const firstAsset = group.assets[0];
                return (
                  <Link
                    className={styles.card}
                    dt-eid={DesignEid.DesignGroupCard}
                    dt-imp-ignore="true"
                    dt-params={dtElementParams({ group_id: group.group_id })}
                    href={buildDesignAssetPath({
                      groupID: group.group_id,
                      assetID: firstAsset.asset_id,
                      groupShortCode: group.group_short_code,
                      assetShortCode: firstAsset.asset_short_code,
                    })}
                    key={group.group_id}
                  >
                    <span className={styles.cardTag}>{group.group_tag}</span>
                    <strong className={styles.cardTitle}>{group.group_name}</strong>
                    <span className={styles.cardDescription}>{group.group_description}</span>
                    <span className={styles.cardArrow} aria-hidden="true">
                      {'\u2192'}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          <footer className={styles.homeFooter}>
            <Link
              className={styles.homeFooterBrand}
              dt-eid={DesignEid.HomeFooterBrandLink}
              dt-imp-ignore="true"
              href="/"
            >
              {logoLoadFailed ? (
                <span className={styles.homeFooterLogoPlaceholder} aria-hidden="true" />
              ) : (
                <img
                  alt="Tencent SSV"
                  className={styles.homeFooterLogo}
                  src={logoEn.src}
                  onError={() => setLogoLoadFailed(true)}
                />
              )}
            </Link>
            <span>SSV Design Center</span>
          </footer>
        </div>
      </section>
    </main>
  );
}
